import axios from 'axios';
import { Platform, DeviceEventEmitter } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Configuration constants (customize as needed)
const DEFAULT_TIMEOUT = 30 * 1000; // 30 seconds
const DEFAULT_PORT = 8080;
const DEFAULT_IP = '192.168.2.120';

/**
 * TeyaPaymentsModule - Handles integration with Teya POSLINK payment terminal
 * Implements payment, refund, and day closing methods
 */
const TeyaPaymentsModule = {
    /**
     * Initialize payment with Teya POSLINK terminal
     * @param {number} amount - Payment amount
     * @param {number} tipAmount - Tip amount (optional)
     * @param {string} orderCode - Order reference code
     * @param {function} onComplete - Callback when payment is completed
     * @param {function} onCancel - Callback when payment is cancelled
     * @returns {Promise} - Promise that resolves when payment is completed
     */
    async initiatePayment(amount, tipAmount = 0, orderCode = '', onComplete, onCancel) {
        console.log("TeyaPaymentsModule.initiatePayment called with amount:", amount);

        try {
            // Get terminal settings from AsyncStorage
            const teyaSettings = await AsyncStorage.getItem('teyaSettings');
            const settings = teyaSettings ? JSON.parse(teyaSettings) : {};

            const ipAddress = settings.ipAddress || DEFAULT_IP;
            const portNumber = settings.portNumber || DEFAULT_PORT;
            const timeout = settings.timeout || DEFAULT_TIMEOUT;

            // Create payment request object
            const paymentRequest = {
                action: "payment",
                amount: Math.round(amount * 100), // Convert to minor units (cents)
                tipAmount: Math.round(tipAmount * 100),
                currency: "HUF",
                reference: orderCode || `ORDER-${Date.now()}`,
                timeout: timeout
            };

            console.log("Sending payment request to Teya terminal:", paymentRequest);

            // Show the payment dialog before making the request
            const dialogData = {
                visible: true,
                amount: amount,
                onComplete: () => {
                    console.log("Teya operation completed manually");
                    if (onComplete) onComplete({ status: 'success', message: 'Operation completed manually' });
                },
                onCancel: () => {
                    console.log("Teya operation cancelled");
                    if (onCancel) onCancel(new Error('Operation cancelled'));
                }
            };

            // Dispatch an event to show the dialog
            if (Platform.OS === 'web') {
                window.dispatchEvent(new CustomEvent('teyaPaymentPending', { detail: dialogData }));
            } else {
                // For React Native, use DeviceEventEmitter
                DeviceEventEmitter.emit('teyaPaymentPending', dialogData);
            }

            // Make request to Teya POSLINK terminal
            const response = await this._makeRequest(ipAddress, portNumber, paymentRequest, timeout);

            console.log("Teya payment response:", response);

            if (response && response.status === 'success') {
                if (onComplete) onComplete(response);
                return response;
            } else {
                const error = new Error(response?.message || 'Payment failed');
                if (onCancel) onCancel(error);
                throw error;
            }
        } catch (error) {
            console.error("Teya payment error:", error);
            if (onCancel) onCancel(error);
            throw error;
        }
    },

    /**
     * Process refund with Teya POSLINK terminal
     * @param {number} amount - Refund amount
     * @param {string} originalTransactionId - Original transaction ID
     * @returns {Promise} - Promise that resolves when refund is completed
     */
    async processRefund(amount, originalTransactionId) {
        console.log("TeyaPaymentsModule.processRefund called with amount:", amount);

        try {
            // Get terminal settings from AsyncStorage
            const teyaSettings = await AsyncStorage.getItem('teyaSettings');
            const settings = teyaSettings ? JSON.parse(teyaSettings) : {};

            const ipAddress = settings.ipAddress || DEFAULT_IP;
            const portNumber = settings.portNumber || DEFAULT_PORT;
            const timeout = settings.timeout || DEFAULT_TIMEOUT;

            // Create refund request object
            const refundRequest = {
                action: "refund",
                amount: Math.round(amount * 100), // Convert to minor units (cents)
                currency: "HUF",
                originalTransactionId: originalTransactionId,
                timeout: timeout
            };

            console.log("Sending refund request to Teya terminal:", refundRequest);

            // Show the payment dialog before making the request
            const dialogData = {
                visible: true,
                amount: amount,
                onComplete: () => {
                    console.log("Teya refund completed manually");
                    // The dialog will be hidden by the component itself
                },
                onCancel: () => {
                    console.log("Teya refund cancelled");
                    // The dialog will be hidden by the component itself
                }
            };

            // Dispatch an event to show the dialog
            if (Platform.OS === 'web') {
                window.dispatchEvent(new CustomEvent('teyaPaymentPending', { detail: dialogData }));
            } else {
                // For React Native, use DeviceEventEmitter
                DeviceEventEmitter.emit('teyaPaymentPending', dialogData);
            }

            // Make request to Teya POSLINK terminal
            const response = await this._makeRequest(ipAddress, portNumber, refundRequest, timeout);

            console.log("Teya refund response:", response);

            if (response && response.status === 'success') {
                return response;
            } else {
                throw new Error(response?.message || 'Refund failed');
            }
        } catch (error) {
            console.error("Teya refund error:", error);
            throw error;
        }
    },

    /**
     * Process day closing with Teya POSLINK terminal
     * @returns {Promise} - Promise that resolves when day closing is completed
     */
    async processDayClosing() {
        console.log("TeyaPaymentsModule.processDayClosing called");

        try {
            // Get terminal settings from AsyncStorage
            const teyaSettings = await AsyncStorage.getItem('teyaSettings');
            const settings = teyaSettings ? JSON.parse(teyaSettings) : {};

            const ipAddress = settings.ipAddress || DEFAULT_IP;
            const portNumber = settings.portNumber || DEFAULT_PORT;
            const timeout = settings.timeout || DEFAULT_TIMEOUT;

            // Create day closing request object
            const dayClosingRequest = {
                action: "dayClosing",
                timeout: timeout
            };

            console.log("Sending day closing request to Teya terminal:", dayClosingRequest);

            // Show the payment dialog before making the request
            const dialogData = {
                visible: true,
                amount: 0, // No amount for day closing
                onComplete: () => {
                    console.log("Teya day closing completed manually");
                    // The dialog will be hidden by the component itself
                },
                onCancel: () => {
                    console.log("Teya day closing cancelled");
                    // The dialog will be hidden by the component itself
                }
            };

            // Dispatch an event to show the dialog
            if (Platform.OS === 'web') {
                window.dispatchEvent(new CustomEvent('teyaPaymentPending', { detail: dialogData }));
            } else {
                // For React Native, use DeviceEventEmitter
                DeviceEventEmitter.emit('teyaPaymentPending', dialogData);
            }

            // Make request to Teya POSLINK terminal
            const response = await this._makeRequest(ipAddress, portNumber, dayClosingRequest, timeout);

            console.log("Teya day closing response:", response);

            if (response && response.status === 'success') {
                return response;
            } else {
                throw new Error(response?.message || 'Day closing failed');
            }
        } catch (error) {
            console.error("Teya day closing error:", error);
            throw error;
        }
    },

    /**
     * Make request to Teya POSLINK terminal
     * @param {string} ipAddress - Terminal IP address
     * @param {number} portNumber - Terminal port number
     * @param {object} requestData - Request data
     * @param {number} timeout - Request timeout
     * @returns {Promise} - Promise that resolves with response
     * @private
     */
    async _makeRequest(ipAddress, portNumber, requestData, timeout) {
        const url = `http://${ipAddress}:${portNumber}/api`;

        const requestOptions = {
            method: "POST",
            url: url,
            headers: {
                "Content-Type": "application/json; charset=UTF-8"
            },
            data: JSON.stringify(requestData),
            timeout: timeout
        };

        try {
            const response = await axios(requestOptions);

            // Hide the dialog when the request is successful
            const hideDialogData = {
                visible: false
            };

            // Dispatch an event to hide the dialog
            if (Platform.OS === 'web') {
                window.dispatchEvent(new CustomEvent('teyaPaymentPending', { detail: hideDialogData }));
            } else {
                DeviceEventEmitter.emit('teyaPaymentPending', hideDialogData);
            }

            return response.data;
        } catch (error) {
            console.error("Teya request error:", error);

            // If we have a UI component listening for events, we can show a dialog
            return new Promise((resolve, reject) => {
                // We don't need to show a new dialog here as it's already shown in initiatePayment
                // Just update the existing dialog with new callbacks
                const dialogData = {
                    onComplete: () => {
                        console.log("Teya operation completed manually");
                        resolve({ status: 'success', message: 'Operation completed manually' });
                    },
                    onCancel: () => {
                        console.log("Teya operation cancelled");
                        reject(new Error('Operation cancelled'));
                    }
                };

                // Dispatch an event to update the dialog
                if (Platform.OS === 'web') {
                    window.dispatchEvent(new CustomEvent('teyaPaymentPending', { detail: dialogData }));
                } else {
                    DeviceEventEmitter.emit('teyaPaymentPending', dialogData);
                }
            });
        }
    }
};

export default TeyaPaymentsModule;
